import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users, TrendingUp, BarChart3, CheckCircle2, Clock,
  Target, Award, UserCheck, Globe
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, FunnelChart, Funnel, LabelList
} from 'recharts';
import { getPublicTestAnalytics } from '../services/api';
import { formatDistanceToNow, parseISO } from 'date-fns';

const CHART_COLORS = ['#6366f1', '#22c55e', '#eab308', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];
const FIT_COLORS = {
  'Strong Fit': '#22c55e',
  'Good Fit': '#3b82f6',
  'Moderate Fit': '#eab308',
  'Risky Fit': '#f97316',
  'Weak Fit': '#ef4444',
  'Unscored': '#64748b',
};
const FUNNEL_COLORS = ['#6366f1', '#8b5cf6', '#22c55e', '#14b8a6'];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-foreground">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          {p.name}: <span className="font-semibold text-foreground">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

export default function PublicTestAnalytics({ jobId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (jobId) fetchAnalytics();
  }, [jobId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const result = await getPublicTestAnalytics(jobId);
      setData(result);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="public-test-analytics-loading">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!data || data.total_applicants === 0) {
    return (
      <Card data-testid="public-test-analytics-empty">
        <CardContent className="py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-indigo-500/10 flex items-center justify-center mx-auto mb-4">
            <Globe className="h-6 w-6 text-indigo-500" />
          </div>
          <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>No Public Test Data Yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {data?.link_active
              ? "Your public test link is active. Share it with candidates to start seeing analytics here."
              : "Generate a public test link first. Candidates who apply through it will show up here."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="public-test-analytics">
      {/* Link Status */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Globe className="h-3.5 w-3.5" />
        Public link is {data.link_active ? (
          <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 text-[10px] px-2 py-0">Active</Badge>
        ) : (
          <Badge variant="secondary" className="text-[10px] px-2 py-0">Inactive</Badge>
        )}
        {data.link_created_at && (
          <span>· Created {formatDistanceToNow(parseISO(data.link_created_at), { addSuffix: true })}</span>
        )}
        {data.link_expires_at && (
          <span>· Expires {new Date(data.link_expires_at).toLocaleDateString()}</span>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border-indigo-500/10" data-testid="kpi-total-applicants">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                <Users className="h-4 w-4 text-indigo-500" />
              </div>
            </div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{data.total_applicants}</p>
            <p className="text-xs text-muted-foreground">Total Applicants</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-500/10" data-testid="kpi-completion-rate">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              </div>
            </div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{data.completion_rate}%</p>
            <p className="text-xs text-muted-foreground">Completion Rate</p>
          </CardContent>
        </Card>
        <Card className="border-amber-500/10" data-testid="kpi-avg-score">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-amber-500" />
              </div>
            </div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{data.avg_resume_score}</p>
            <p className="text-xs text-muted-foreground">Avg Resume Score</p>
          </CardContent>
        </Card>
        <Card className="border-violet-500/10" data-testid="kpi-top-score">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <Award className="h-4 w-4 text-violet-500" />
              </div>
            </div>
            <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{data.top_resume_score}</p>
            <p className="text-xs text-muted-foreground">Top Resume Score</p>
          </CardContent>
        </Card>
      </div>

      {/* Funnel + Fit Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Funnel */}
        <Card data-testid="funnel-chart-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Target className="h-4 w-4 text-indigo-500" /> Applicant Funnel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.funnel.map((item, i) => {
                const maxCount = data.funnel[0].count || 1;
                const pct = Math.round((item.count / maxCount) * 100);
                return (
                  <div key={item.stage}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium">{item.stage}</span>
                      <span className="text-xs text-muted-foreground font-mono">{item.count} ({pct}%)</span>
                    </div>
                    <div className="h-7 rounded-md bg-muted/40 overflow-hidden">
                      <div
                        className="h-full rounded-md transition-all duration-700 flex items-center pl-2"
                        style={{ width: `${Math.max(pct, 4)}%`, backgroundColor: FUNNEL_COLORS[i] }}
                      >
                        {pct > 15 && <span className="text-[10px] font-bold text-white">{item.count}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Fit Breakdown */}
        <Card data-testid="fit-breakdown-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <UserCheck className="h-4 w-4 text-indigo-500" /> Fit Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="50%" height={180}>
                <PieChart>
                  <Pie data={data.fit_breakdown} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={35} outerRadius={70} paddingAngle={2}>
                    {data.fit_breakdown.map((entry, i) => (
                      <Cell key={i} fill={FIT_COLORS[entry.label] || CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {data.fit_breakdown.map((item, i) => (
                  <div key={item.label} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: FIT_COLORS[item.label] || CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="text-xs flex-1 truncate">{item.label}</span>
                    <span className="text-xs font-mono font-medium">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Score Distribution Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card data-testid="resume-score-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <BarChart3 className="h-4 w-4 text-indigo-500" /> Resume Score Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.score_distribution} barSize={32}>
                <XAxis dataKey="range" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Candidates" radius={[4, 4, 0, 0]}>
                  {data.score_distribution.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card data-testid="interview-score-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <BarChart3 className="h-4 w-4 text-emerald-500" /> Interview Score Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.avg_interview_score > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.interview_score_distribution} barSize={32}>
                  <XAxis dataKey="range" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" name="Interviews" radius={[4, 4, 0, 0]}>
                    {data.interview_score_distribution.map((_, i) => (
                      <Cell key={i} fill={CHART_COLORS[(i + 2) % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center">
                <p className="text-sm text-muted-foreground">No completed interviews yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Applicants Table */}
      <Card data-testid="recent-applicants-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Clock className="h-4 w-4 text-indigo-500" /> Recent Applicants
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground">Name</th>
                  <th className="text-left py-2 px-2 font-medium text-muted-foreground">Email</th>
                  <th className="text-center py-2 px-2 font-medium text-muted-foreground">Resume Score</th>
                  <th className="text-center py-2 px-2 font-medium text-muted-foreground">Fit</th>
                  <th className="text-center py-2 px-2 font-medium text-muted-foreground">Interview</th>
                  <th className="text-center py-2 px-2 font-medium text-muted-foreground">Interview Score</th>
                  <th className="text-right py-2 px-2 font-medium text-muted-foreground">Applied</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_applicants.map((a) => (
                  <tr key={a.candidate_id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-2.5 px-2 font-medium">{a.name}</td>
                    <td className="py-2.5 px-2 text-muted-foreground">{a.email}</td>
                    <td className="py-2.5 px-2 text-center">
                      {a.resume_score != null ? (
                        <span className="font-mono font-semibold" style={{
                          color: a.resume_score >= 70 ? '#22c55e' : a.resume_score >= 40 ? '#eab308' : '#ef4444'
                        }}>{a.resume_score}</span>
                      ) : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      {a.fit_label ? (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0" style={{
                          borderColor: FIT_COLORS[a.fit_label] || '#64748b',
                          color: FIT_COLORS[a.fit_label] || '#64748b'
                        }}>{a.fit_label}</Badge>
                      ) : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <Badge variant={
                        a.interview_status === 'completed' ? 'default' :
                        a.interview_status === 'in_progress' ? 'secondary' : 'outline'
                      } className="text-[10px] px-1.5 py-0">
                        {a.interview_status}
                      </Badge>
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      {a.interview_score != null ? (
                        <span className="font-mono font-semibold" style={{
                          color: a.interview_score >= 70 ? '#22c55e' : a.interview_score >= 40 ? '#eab308' : '#ef4444'
                        }}>{a.interview_score}</span>
                      ) : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="py-2.5 px-2 text-right text-muted-foreground">
                      {a.applied_at ? formatDistanceToNow(parseISO(a.applied_at), { addSuffix: true }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
